/**
 * Type definitions for the Residents Tab
 *
 * Includes types for resident summaries, risk factors, and clinical data sections.
 */

// ============================================================================
// Risk Level Enums
// ============================================================================

export type RiskLevel = "HIGH" | "MEDIUM" | "LOW" | "UNKNOWN";

// ============================================================================
// Resident Summary (Main Table)
// ============================================================================

export interface NewInsights {
  vital_changes: boolean;
  progress_notes: boolean;
  high_risk_meds: boolean;
  low_risk_meds: boolean;
  infections: boolean;
  falls: boolean;
  significant_change: boolean;
  adl_increase: boolean | null;       // null = no GG data available
  locomotion_decline: boolean | null;  // null = no GG data available
}

export interface PrimaryRiskFactors {
  indwelling_catheter: boolean | null;
  previous_falls: boolean | null;
  receiving_antipsychotic: boolean | null;
  admitted_lt_30_days: boolean;
  long_term_infections: boolean;
}

export interface ResidentSummary {
  resident_id: string;
  wq_id?: string;  // WorkQueue ID (used for all API calls)
  facility_id?: string;  // Facility ID for multi-facility display
  facility_name?: string;  // Facility name if available
  resident_name: string;
  health_record_number?: string | null;  // Medical record number (HR #)
  room: string | null;
  building_id?: string | null;
  station_id?: string | null;
  date_of_birth: string | null;
  risk_level: RiskLevel;
  days_since_last_review: number | null;
  cq_notes_total: number; // Total CQ notes
  cq_notes_unreviewed: number; // Unreviewed CQ notes
  new_insights: NewInsights;
  primary_risk_factors: PrimaryRiskFactors;
  data_sources_used?: {
    mds_id?: string | null;
    mds_isc_code?: string | null;
    mds_completed_date?: string | null;
    mds_sections?: string[];
  };
  risk_by_period?: Record<string, PeriodRiskData>;
}

export interface ResidentSummaryListResponse {
  residents: ResidentSummary[];
  count: number;
}

// ============================================================================
// Risk Factor Summary (Detail Dialog Section)
// ============================================================================

export interface RiskFactorDetail {
  name: string;
  level: RiskLevel;
  score: number | null;
  details: string[];             // Detail strings ("Label: Value")
  care_plan_status: string;      // "in-care-plan" | "not-care-planned"
}

export interface AiSummaryPeriodData {
  summary: string | null;
  reasoning: { factor: string; justification: string }[] | null;
  status: "success" | "failed" | "disabled" | "skipped";
}

export interface RiskFactorSummaryResponse {
  resident_id: string;
  overall_score: number | null;
  overall_level: RiskLevel;
  last_assessed: string | null;
  last_infection_date: string | null;
  factors: RiskFactorDetail[];
  ai_summary: string | null;  // AI-generated clinical summary
  ai_summary_reasoning?: { factor: string; justification: string }[] | null;
  ai_summary_status?: "success" | "failed" | "disabled" | "skipped";
  ai_summary_by_period?: Record<string, AiSummaryPeriodData>;
  calculation_status?: "success" | "partial" | "failed";
  significant_change_detected?: boolean | null;
  significant_change_reasoning?: string | null;
}

// ============================================================================
// Care Queue Notes (Detail Dialog Section)
// ============================================================================

export interface CareQueueNote {
  id: string | null;
  timestamp: string;
  author: string;
  content: string;
  high_priority?: boolean;
}

export interface CareQueueNotesResponse {
  resident_id: string;
  notes: CareQueueNote[];
  count: number;
}

// ============================================================================
// Create Care Queue Note (Request/Response)
// ============================================================================

export interface CreateCareQueueNoteRequest {
  content: string;
  high_priority: boolean;
}

export interface CreateCareQueueNoteResponse {
  resident_id: string;
  note: CareQueueNote;
}

// ============================================================================
// Progress Notes (Detail Dialog Section)
// ============================================================================

export interface ProgressNote {
  id: string | null;
  date: string;
  time: string | null;
  department: string | null;
  author: string;
  credentials: string | null;
  risk_tags: string[];
  content: string;
  in_care_plan: boolean;
  reasoning: string | null;
}

export interface ProgressNotesResponse {
  resident_id: string;
  as_of: string;
  window_days: number;
  notes: ProgressNote[];
  count: number;
}

// ============================================================================
// Vital Signs (Detail Dialog Section)
// ============================================================================

export interface VitalSign {
  date: string;
  blood_pressure: string | null;
  bp_type: string | null;
  heart_rate: number | null;
  pulse_type: string | null;
  temperature: number | null;
  temperature_unit: string;
  temperature_method: string | null;
  oxygen_saturation: number | null;
  o2_sat_type: string | null;
  respiration: number | null;
  weight: number | null;
  weight_unit: string;
  weight_type: string | null;
  height: number | null;
  height_unit: string;
  pain_level: number | null;
  bmi: number | null;
  blood_glucose: number | null;
  bg_type: string | null;
  level_of_consciousness: string | null;
  notes: string | null;
  source: string | null;
  recorded_by: string | null;
  event_id: number | null;
  is_struck?: boolean;
}

export interface VitalSignsResponse {
  resident_id: string;
  as_of: string;
  window_days: number;
  readings: VitalSign[];
  // Most recent weight readings captured before the window. Weights are recorded
  // infrequently, so these backfill the weight trend up to the reference set of
  // the 6 most recent charted weights, however long ago they were charted.
  // Not part of `readings`/`count` (the in-window table).
  prior_weights?: VitalSign[];
  count: number;
}

// ============================================================================
// Medications (Detail Dialog Section)
// ============================================================================

export type MedicationStatus = "ACTIVE" | "DISCONTINUED" | "ON_HOLD";

export interface Administration {
  admin_number: number;
  dose: string | null;
  time_code: string | null;
  time_code_description: string | null;
  description: string | null;
}

export interface Medication {
  id: string | null;
  name: string;
  generic_name: string | null;
  drug_class: string | null;
  is_high_risk: boolean;
  dosage: string | null;
  frequency: string | null;
  route: string | null;
  route_description: string | null;
  prescriber: string | null;
  status: MedicationStatus;
  start_date: string | null;
  end_date: string | null;
  indication: string | null;
  administrations: Administration[];
  is_new?: boolean;  // True if started within the New Insights lookback window
}

export interface MedicationsResponse {
  resident_id: string;
  as_of: string;
  new_threshold_days: number;
  medications: Medication[];
  count: number;
}

// ============================================================================
// Infections (Detail Dialog Section)
// ============================================================================

export type InfectionStatus = "ACTIVE" | "RESOLVED" | "MONITORING";

export interface TestResult {
  test_id: string | null;
  method: string | null;
  type: string | null;
  date: string | null;
  organism: string | null;
  results: string | null;
  comments: string | null;
}

export interface Treatment {
  treatment_id: string | null;
  date: string | null;
  order: string | null;
  comments: string | null;
}

export interface Isolation {
  required: boolean;
  type: string | null;
  no_treatments_ordered: boolean;
  comments: string | null;
}

export interface Infection {
  id: string | null;
  type: string;
  onset_date: string | null;
  status: InfectionStatus;
  resolution_date: string | null;
  notes: string | null;
  signs: string[];
  tests: TestResult[];
  treatments: Treatment[];
  isolation: Isolation | null;
  in_care_plan: boolean;
}

export interface InfectionsResponse {
  resident_id: string;
  as_of: string;
  infections: Infection[];
  count: number;
}

// ============================================================================
// Orders (Detail Dialog Section)
// ============================================================================

export interface InfoOrder {
  order_desc: string | null;
  order_code: string | null;
  is_new: boolean;
}

export interface LookupOrder {
  order_desc: string | null;
  order_code: string | null;
}

export interface ResidentOrders {
  resident_id: string;
  info_orders: InfoOrder[];
  pending_orders: LookupOrder[];
  awaiting_noted_sig_orders: LookupOrder[];
  count: number;
}

// ============================================================================
// Fall Incidents Types
// ============================================================================

export interface IncidentType {
  code: string;
  description: string;
  other_description: string | null;
}

export interface IncidentLocation {
  description: string | null;
  room_number: string | null;
}

export interface MinorInjuryObservation {
  injury_types: string[] | null;
  injury_locations: string[] | null;
}

export interface MajorInjuryObservation {
  injury_types: string[] | null;
  injury_locations: string[] | null;
}

export interface NeurologicalObservation {
  assessment_completed: boolean;
  assessment_status: number | null;
}

export interface IncidentObservations {
  minor_injuries: MinorInjuryObservation | null;
  major_injuries: MajorInjuryObservation | null;
  psychological_symptoms: string[] | null;
  neurological: NeurologicalObservation | null;
}

export interface FallIncident {
  id: string;
  incident_date: string;
  incident_type: IncidentType;
  location: IncidentLocation | null;
  description: string | null;
  reported_by: string | null;
  reported_at: string | null;
  observed_by: string | null;
  assigned_to: string | null;
  notifications_sent: string[];
  observations: IncidentObservations | null;
}

export interface FallIncidentsResponse {
  resident_id: string;
  as_of: string;
  window_days: number;
  incident_type_filter: string | null;
  incidents: FallIncident[];
  count: number;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Calculate age from a date of birth string.
 * Supports both YYYY-MM-DD and MM/DD/YYYY formats.
 */
export function calculateAge(dateOfBirth: string | null): number | null {
  if (!dateOfBirth) return null;

  let birth: Date;

  // Try parsing YYYY-MM-DD format
  if (dateOfBirth.includes("-") && dateOfBirth.length >= 10) {
    birth = new Date(dateOfBirth);
  }
  // Try parsing MM/DD/YYYY format
  else if (dateOfBirth.includes("/")) {
    const parts = dateOfBirth.split("/");
    if (parts.length === 3) {
      const month = parseInt(parts[0], 10) - 1; // Months are 0-indexed
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      birth = new Date(year, month, day);
    } else {
      return null;
    }
  } else {
    return null;
  }

  if (isNaN(birth.getTime())) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age >= 0 ? age : null;
}

/**
 * Get risk level display color based on DLS guidelines.
 * Uses muted/subdued colors for LOW and MEDIUM levels.
 */
export function getRiskLevelColor(level: RiskLevel): {
  bg: string;
  text: string;
  border?: string;
} {
  switch (level) {
    case "HIGH":
      return { bg: "#FEE2E2", text: "#B02828", border: "#B02828" };
    case "MEDIUM":
      return { bg: "#FEF3C7", text: "#D97706", border: "#D97706" };
    case "LOW":
      return { bg: "#DCFCE7", text: "#15803D", border: "#15803D" };
    case "UNKNOWN":
    default:
      return { bg: "#BBC3C8", text: "#1B3647" };
  }
}

/**
 * Get risk level display label.
 */
export function getRiskLevelLabel(level: RiskLevel): string {
  switch (level) {
    case "HIGH":
      return "HIGH";
    case "MEDIUM":
      return "MEDIUM";
    case "LOW":
      return "LOW";
    case "UNKNOWN":
    default:
      return "—";
  }
}

// ============================================================================
// Backend Risk Data Types (from /api/v1/residents endpoint)
// ============================================================================

export interface RiskFactors {
  high_risk_medications: boolean;
  progress_notes_concerns: boolean;
  previous_falls: boolean | null;
  infections: boolean;
  long_term_infections: boolean;
  vital_changes: boolean;
  indwelling_catheter: boolean | null;
  low_risk_medications: boolean;
  receiving_antipsychotic: boolean | null;
  admitted_less_30_days: boolean;
  functional_decline: boolean;
  adl_increase: boolean | null;
  locomotion_decline: boolean | null;
}

export interface RiskScoreBreakdown {
  high_impact_score: number;
  medium_impact_score: number;
  low_impact_score: number;
  total_score: number;
}

export interface PeriodRiskData {
  risk_factors: RiskFactors;
  risk_score_breakdown: RiskScoreBreakdown;
  overall_risk_level: RiskLevel;
}

export interface JobResultsSummary {
  high_risk_count: number;
  medium_risk_count: number;
  low_risk_count: number;
  total_processed: number;
  total_errors: number;
}

export interface ResidentRiskData {
  resident_id: string;
  wq_id?: string;  // WorkQueue globally-unique resident ID
  client_id: string;
  facility_id: string;
  resident_name: string;
  health_record_number?: string | null;  // Medical record number (HR #)
  room: string | null;
  building_id?: string | null;
  station_id?: string | null;
  unit: string | null;
  admission_date: string | null;
  payer: string | null;
  date_of_birth: string | null;
  risk_factors: RiskFactors;
  risk_score_breakdown: RiskScoreBreakdown;
  overall_risk_level: RiskLevel;
  last_calculated_at: string;
  cq_notes_total: number; // Total CQ notes
  cq_notes_unreviewed: number; // Unreviewed CQ notes
  last_reviewed_at: string | null;
  last_reviewed_by: string | null;
  needs_review: boolean;
  data_sources_used?: {
    mds_id?: string | null;
    mds_isc_code?: string | null;
    mds_completed_date?: string | null;
    mds_sections?: string[];
    vitals_count?: number;
    notes_count?: number;
    incidents_count?: number;
    medications_count?: number;
    last_mds_date?: string | null;
    llm_notes_analyzed?: number;
  };
  significant_change_detected?: boolean | null;
  significant_change_reasoning?: string | null;
  risk_by_period?: Record<string, PeriodRiskData>;
}

export interface GetResidentsResponse {
  residents: ResidentRiskData[];
  total: number;
  limit: number;
  offset: number;
  summary: JobResultsSummary;
}

/**
 * Compute days between a date string and now. Returns null if date is null.
 */
function daysSince(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const then = new Date(dateStr);
  if (isNaN(then.getTime())) return null;
  const now = new Date();
  const diffMs = now.getTime() - then.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Map a ResidentRiskData (from backend) to a ResidentSummary (for UI components).
 */
export function mapRiskDataToSummary(data: ResidentRiskData): ResidentSummary {
  const rf = data.risk_factors;
  // MDS-derived fields should only show True/False when a completed MDS exists.
  // Without a completed MDS, show N/A (null) regardless of what the backend stored
  // (an in-progress MDS with empty sections can incorrectly produce False values).
  const hasMdsData = !!data.data_sources_used?.mds_completed_date;
  return {
    resident_id: data.resident_id,
    wq_id: data.wq_id,
    facility_id: data.facility_id,
    resident_name: data.resident_name,
    health_record_number: data.health_record_number,
    room: data.room,
    building_id: data.building_id,
    station_id: data.station_id,
    date_of_birth: data.date_of_birth,
    risk_level: data.overall_risk_level,
    days_since_last_review: daysSince(data.last_reviewed_at),
    cq_notes_total: data.cq_notes_total ?? 0,
    cq_notes_unreviewed: data.cq_notes_unreviewed ?? 0,
    new_insights: {
      vital_changes: rf.vital_changes,
      progress_notes: rf.progress_notes_concerns,
      high_risk_meds: rf.high_risk_medications,
      low_risk_meds: rf.low_risk_medications,
      infections: rf.infections,
      falls: rf.previous_falls === true,
      significant_change: data.significant_change_detected ?? false,
      // NOT gated by hasMdsData: these come from the EHR functional-abilities
      // (GG-code) trend data, which is independent of MDS completion.
      adl_increase: rf.adl_increase ?? null,
      locomotion_decline: rf.locomotion_decline ?? null,
    },
    primary_risk_factors: {
      indwelling_catheter: hasMdsData ? rf.indwelling_catheter : null,
      previous_falls: rf.previous_falls,
      receiving_antipsychotic: hasMdsData ? rf.receiving_antipsychotic : null,
      admitted_lt_30_days: rf.admitted_less_30_days,
      long_term_infections: rf.long_term_infections,
    },
    data_sources_used: data.data_sources_used ? {
      mds_id: data.data_sources_used.mds_id,
      mds_isc_code: data.data_sources_used.mds_isc_code,
      mds_completed_date: data.data_sources_used.mds_completed_date,
      mds_sections: data.data_sources_used.mds_sections,
    } : undefined,
    risk_by_period: data.risk_by_period,
  };
}

/**
 * Re-resolve a resident against one lookback window from `risk_by_period`.
 *
 * The roster row's top-level `risk_level` / `new_insights` / `primary_risk_factors`
 * are all-time values. Both surfaces that show resident detail — the Care Queue
 * dashboard and the embeddable resident-detail widget — display them for the
 * *selected* window instead, so this lives here rather than in either one.
 *
 * Residents with no entry for that window are returned unchanged.
 */
export function resolveResidentForPeriod(
  resident: ResidentSummary,
  periodDays: number | string,
): ResidentSummary {
  const period = resident.risk_by_period?.[String(periodDays)];
  if (!period) return resident;

  const rf = period.risk_factors;
  return {
    ...resident,
    risk_level: period.overall_risk_level,
    new_insights: {
      vital_changes: rf.vital_changes,
      progress_notes: rf.progress_notes_concerns,
      high_risk_meds: rf.high_risk_medications,
      low_risk_meds: rf.low_risk_medications,
      infections: rf.infections,
      falls: rf.previous_falls === true,
      significant_change: resident.new_insights.significant_change,
      adl_increase: rf.adl_increase ?? resident.new_insights.adl_increase,
      locomotion_decline:
        rf.locomotion_decline ?? resident.new_insights.locomotion_decline,
    },
    primary_risk_factors: {
      indwelling_catheter: rf.indwelling_catheter,
      previous_falls: rf.previous_falls,
      receiving_antipsychotic: rf.receiving_antipsychotic,
      admitted_lt_30_days: rf.admitted_less_30_days,
      long_term_infections: rf.long_term_infections ?? false,
    },
  };
}

// ============================================================================
// Resident Notes (real resident-notes endpoint)
// ============================================================================

export type NoteType = "RISK_REVIEW" | "CARE_PLAN" | "GENERAL";

export interface ResidentNote {
  note_id: string;
  resident_id: string;
  client_id: string;
  facility_id: string;
  note_type: NoteType;
  content: string;
  tags: string[];
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  updated_at: string | null;
  is_deleted: boolean;
}

export interface ListNotesResponse {
  notes: ResidentNote[];
  total: number;
  limit: number;
  offset: number;
  resident_id: string;
  metadata: {
    total_notes_count: number;
    cq_notes_total: number; // Total CQ notes
  cq_notes_unreviewed: number; // Unreviewed CQ notes
    notes_after_last_review: number;
    last_reviewed_at: string | null;
  };
}

export interface CreateResidentNoteRequest {
  // Required fields (sent in request body)
  note_type: NoteType;
  content: string;

  // Optional fields
  priority?: string;  // "HIGH" | "MEDIUM" | "LOW"
  tags?: string[];

  // Note: resident_id, client_id, facility_id auto-populated by backend from path/token
}

export interface CreateResidentNoteResponse {
  note_id: string;
  created_at: string;
  message: string;
}

// ============================================================================
// Review Types
// ============================================================================

export type ReviewAction =
  | "NO_ACTION"
  | "CARE_PLAN_UPDATED"
  | "PHYSICIAN_NOTIFIED"
  | "FAMILY_CONTACTED"
  | "INTERVENTION_INITIATED"
  | "MONITORING_INCREASED";

export interface UnifiedReviewRequest {
  reviewed_by: string;
  review_notes?: string;
  action_taken: ReviewAction;
}

export interface UnifiedReviewResponse {
  resident_id: string;
  reviewed_at: string;
  collections_updated: string[];
  notes_reviewed_count: number;
  message: string;
}

// ============================================================================
// Hide Resident (Request/Response)
// ============================================================================

export interface HideResidentRequest {
  days: number;
}

export interface HideResidentResponse {
  resident_id: string;
  hidden_until: string;
  hidden_by: string;
  message: string;
}

// ============================================================================
// Risk Trend Types
// ============================================================================

export type RiskTrendValue = RiskLevel | null;

export interface RiskTrendResponse {
  resident_id: string;
  trend: RiskTrendValue[];
  trend_by_period: Record<string, RiskTrendValue[]>;
  records: Record<string, unknown>[];
  count: number;
}

// ============================================================================
// Resident Modal Types (RESIDENT-MODAL-001)
// ============================================================================

/**
 * Care plan status for a risk factor
 */
export type CarePlanStatus = 'in-care-plan' | 'not-care-planned' | 'pending';

/**
 * Detail item for a risk factor
 */
export interface RiskFactorDetailItem {
  label: string;
  value: string;
}

/**
 * Risk Factor interface for Primary Risk Factors Summary
 * Represents a clinical risk factor with associated details and care plan status
 */
export interface RiskFactor {
  /** Unique identifier for the risk factor */
  id: string;

  /** Display title of the risk factor */
  title: string;

  /** Lucide-react icon name (e.g., 'Droplet', 'Calendar', 'AlertTriangle', 'Pill') */
  icon: string;

  /** Whether this risk factor is currently active */
  isActive: boolean;

  /** Array of detail items (label-value pairs) */
  details: RiskFactorDetailItem[];

  /** Current care plan status */
  carePlanStatus: CarePlanStatus;
}

/**
 * Resident Modal Data interface
 * Contains all data needed to display the resident modal
 */
export interface ResidentModalData {
  /** Resident unique identifier */
  id: string;

  /** Resident full name */
  name: string;

  /** Room number */
  room: string;

  /** Resident age */
  age: number;

  /** Facility name */
  facility: string;

  /** Array of primary risk factors */
  primaryRiskFactors: RiskFactor[];
}

// =============================================================================
// AI Feedback Types (Thumbs Up/Down)
// =============================================================================

export type AiFeedbackContentType =
  | "ai_summary"
  | "note_reasoning"
  | "medication_classification"
  | "note_risk_classification";

export type AiFeedbackSentiment = "positive" | "negative";

export interface AiFeedbackCreateRequest {
  content_type: AiFeedbackContentType;
  sentiment: AiFeedbackSentiment;
  comment?: string | null;
  source_item_id?: string | null;
  risk_document_id: string;
  lookback_period?: number;
}

export interface AiFeedbackCreateResponse {
  feedback_id: string;
  created_at: string;
}

export interface AiFeedbackItem {
  feedback_id: string;
  content_type: AiFeedbackContentType;
  sentiment: AiFeedbackSentiment;
  comment?: string | null;
  source_item_id?: string | null;
  created_at: string;
}

export interface AiFeedbackListResponse {
  feedback: AiFeedbackItem[];
}

// ========================================================================
// Functional Abilities (Section GG) — 3-Day Trend
// ========================================================================

export type FunctionalCode = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 9 | 10 | 88 | null;

export type ShiftPair = [FunctionalCode, FunctionalCode];

// GG0130 Self-Care Activities
export const SELF_CARE_ABILITIES = [
  "Eating",
  "Oral Hygiene",
  "Toileting Hygiene",
  "Shower/bathe self",
  "Upper body dressing",
  "Lower body dressing",
  "Putting on/taking off footwear",
  "Personal hygiene",
] as const;

// GG0170 Mobility Activities
export const MOBILITY_ABILITIES = [
  "Roll left and right",
  "Sit to lying",
  "Lying to sitting on side of bed",
  "Sit to stand",
  "Chair/bed-to-chair transfer",
  "Toilet transfer",
  // WellSky POC2 ADL observation (not a standard MDS GG0170 item). Shown so the
  // row isn't missing; populates once the EHR maps its GG code (NETSOL-11680).
  "Tub/shower transfer",
  "Car transfer",
  "Walk 10 feet",
] as const;

export const FUNCTIONAL_ABILITIES = [
  ...SELF_CARE_ABILITIES,
  ...MOBILITY_ABILITIES,
] as const;

export type FunctionalAbility = (typeof FUNCTIONAL_ABILITIES)[number];

export interface TherapyScore {
  therapy_date: string | null;
  score: FunctionalCode;
}

export interface NursingDay {
  nursing_date: string | null;
  AM: FunctionalCode;
  PM: FunctionalCode;
}

export interface FunctionalAbilityRow {
  ability: FunctionalAbility;
  therapy: TherapyScore;
  nursing: NursingDay[];
}

export interface FunctionalAbilitiesResponse {
  resident_id: string;
  abilities: FunctionalAbilityRow[];
}

export const FUNCTIONAL_CODE_LABELS: Record<number, string> = {
  6: "Independent",
  5: "Setup/clean-up",
  4: "Supervision/touching",
  3: "Partial/moderate",
  2: "Substantial/maximal",
  1: "Dependent",
  7: "Refused",
  9: "N/A",
  10: "Environmental",
  88: "Medical/safety",
};

export function isFunctionalScore(
  code: FunctionalCode
): code is 1 | 2 | 3 | 4 | 5 | 6 {
  return code !== null && code >= 1 && code <= 6;
}
